// As partes do alvo que NÃO seguem a regra em edição (design D13, `overrides` da prévia de
// impacto): um insumo, grupo Ampli ou grupo do ERP com regra própria dentro do serviço,
// subetapa ou etapa. O motor dá precedência à regra mais específica, então essas partes
// ficam com a forma delas — e o cliente precisa vê-las para analisar e corrigir, não
// descobrir depois na curva.
//
// Só leitura e atalho: "Editar regra" abre o editor da regra que governa a parte.
import { describeProfile } from "./profile";
import { TargetValue } from "./RuleTargetPicker";

const kindLabel = (target) => {
  const key = String(target?.key ?? "");
  if (target?.kind === "resource") return "Insumo";
  if (key.startsWith("resource_group:ampli:")) return "Grupo Ampli";
  if (key.startsWith("resource_group:")) return "Grupo do ERP";
  if (target?.kind === "node") return "EAP";
  return "Regra";
};

const profileText = (profile) => {
  try {
    return describeProfile(profile);
  } catch {
    return "Forma indisponível";
  }
};

const OverrideRow = ({ override, onEdit }) => (
  <li className="ampli-rule-overrides__row">
    <div className="ampli-rule-overrides__main">
      <span className="ampli-rule-overrides__kind">
        {kindLabel(override.target)}
      </span>
      <strong>{override.target?.label ?? override.ruleKey}</strong>
      <span className="ampli-rule-overrides__detail">
        {`${profileText(override.profile)} · ${override.serviceCount} ${
          override.serviceCount === 1 ? "serviço" : "serviços"
        }`}
      </span>
    </div>
    <span className="ampli-rule-overrides__value">
      <TargetValue cents={override.redistributedCents} />
    </span>
    {onEdit && override.ruleId ? (
      <button
        type="button"
        className="button button-outline small"
        aria-label={`Editar regra de ${
          override.target?.label ?? override.ruleKey
        }`}
        onClick={() => onEdit(override)}
      >
        Editar regra
      </button>
    ) : null}
  </li>
);

// Um estado por vez: carregando, falha, nenhuma exceção ou a lista.
const OverridesBody = ({ status, overrides, onEdit }) => {
  if (status === "loading") {
    return (
      <p className="card-label" role="status">
        Verificando insumos com regra diferente…
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="card-label" role="status">
        Não foi possível verificar os insumos com regra própria agora.
      </p>
    );
  }
  if (overrides.length === 0) {
    return (
      <p className="card-label" data-testid="rule-overrides-empty">
        Nenhum insumo deste alvo tem regra diferente: todo o valor segue esta
        regra.
      </p>
    );
  }
  return (
    <>
      <p className="ampli-rule-overrides__lead">
        {"Estas partes seguem a regra própria delas, que é mais específica, e "}
        <strong>não mudam com esta regra</strong>
        {". Confira se a forma de cada uma faz sentido e corrija se precisar."}
      </p>
      <ul>
        {overrides.map((override) => (
          <OverrideRow
            key={override.ruleKey}
            override={override}
            onEdit={onEdit}
          />
        ))}
      </ul>
    </>
  );
};

const RuleOverridesSection = ({ impact, onEditOverride }) => {
  if (!impact || impact.status === "idle" || impact.status === "unavailable")
    return null;
  const overrides = impact.answer?.overrides ?? [];
  return (
    <section
      className="ampli-rule-overrides"
      aria-labelledby="ampli-rule-overrides-title"
    >
      <h3 id="ampli-rule-overrides-title">
        Insumos com regra própria neste alvo
      </h3>
      <OverridesBody
        status={impact.status}
        overrides={overrides}
        onEdit={onEditOverride}
      />
    </section>
  );
};

export default RuleOverridesSection;
